import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Grid,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { DishForm } from './types';
import { Dish } from 'types';
import { useLocations } from 'features/locations/api/getLocations';
import { useCreateDish } from './api/createDish';
import { useUpdateDish } from './api/updateDish';
import { useSatellites } from '../satellites/api/getSatellites';
import { useUpdateSatelliteConnections } from './api/updateDishSatelliteConnections';

interface Props {
  open: boolean;
  handleClose: () => void;
  dish: Dish | undefined;
}

const initialFormState: DishForm = {
  id: null,
  name: '',
  size: '',
  location_id: '',
  coord_system: 0,
  coord_1: '',
  coord_2: '',
  satellite_ids: [],
};

const DishesDialog = ({ open, handleClose, dish }: Props) => {
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    formState: { errors },
  } = useForm({ defaultValues: initialFormState });
  const { data: locations } = useLocations({});
  const { data: satellites } = useSatellites({});
  const createDish = useCreateDish({});
  const updateDish = useUpdateDish({});
  const updateSatelliteConnection = useUpdateSatelliteConnections({});

  useEffect(() => {
    if (dish) {
      setValue('id', dish.id);
      setValue('name', dish.name);
      setValue('size', dish?.size ? '' + dish.size / 100 : '');
      setValue('coord_system', 0);
      setValue('coord_1', dish.coord_1 ? dish.coord_1 : '');
      setValue('coord_2', dish.coord_2 ? dish.coord_2 : '');
      setValue('location_id', dish.location_id ? '' + dish.location_id : '');
      setValue(
        'satellite_ids',
        dish?.satellites?.map((satellite) => satellite.id) || []
      );
    } else reset();
  }, [dish, open, reset, setValue]);

  const onSubmit = async () => {
    const formState = getValues();
    let id = watch('id');
    if (!id) {
      const dish: any = await createDish.mutateAsync({
        ...formState,
        size: formState.size ? Math.round(Number(formState.size) * 100) : null,
        location_id:
          formState.location_id && formState.location_id !== '0'
            ? Number(formState.location_id)
            : null,
      });
      id = dish.data.id as number;
    } else {
      await updateDish.mutateAsync({
        data: {
          ...formState,
          size: formState.size ? Math.round(Number(formState.size) * 100) : null,
          location_id:
            formState.location_id && formState.location_id !== '0'
              ? Number(formState.location_id)
              : null,
        },
        dishId: id,
      });
    }

    await updateSatelliteConnection.mutateAsync({
      dish_id: id,
      satellite_ids: formState.satellite_ids,
    });

    reset();
    handleClose();
  };

  const handleCancel = () => {
    reset();
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>{watch('id') ? 'Edit Dish' : 'New Dish'}</DialogTitle>
      <DialogContent>
        <Grid
          container
          spacing={1}
          direction='column'
          style={{ paddingTop: '.4rem' }}
        >
          <Grid item xs>
            <Controller
              name='name'
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <TextField
                  id='name'
                  label='Dish Name'
                  name='name'
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  fullWidth
                  required
                  error={!!errors.name}
                />
              )}
            />
          </Grid>
          <Grid item xs>
            <Controller
              name='size'
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field: { onChange, value } }) => (
                <TextField
                  id='size'
                  label='Dish Size (meters)'
                  name='size'
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  fullWidth
                  required
                  inputProps={{ type: 'number' }}
                  error={!!errors.size}
                />
              )}
            />
          </Grid>
          <Grid item xs>
            <Controller
              name='location_id'
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControl fullWidth>
                  <InputLabel id='location-select-label'>Location</InputLabel>
                  <Select
                    labelId='location-select-label'
                    label='Location'
                    id='location_id'
                    name='location_id'
                    onChange={(e) => onChange(e.target.value)}
                    value={value}
                  >
                    {locations &&
                      locations?.map((location) => (
                        <MenuItem key={location.id} value={location.id}>
                          {location.name}
                        </MenuItem>
                      ))}
                    <MenuItem key='0' value={'0'}>
                      None
                    </MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs>
            <Controller
              name='satellite_ids'
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControl fullWidth>
                  <InputLabel id='satellite-select-label'>
                    Satellites
                  </InputLabel>
                  <Select
                    labelId='satellite-select-label'
                    label='Satellite'
                    id='satellite_ids'
                    multiple
                    name='satellite_ids'
                    renderValue={(selected: any) =>
                      satellites
                        ?.filter((satellite) =>
                          getValues('satellite_ids').includes(satellite.id)
                        )
                        .map((satellite) => satellite.name)
                        .join(', ') || 'Satellites'
                    }
                    onChange={(e) => onChange(e.target.value)}
                    value={value}
                  >
                    {satellites &&
                      satellites?.map((satellite) => (
                        <MenuItem key={satellite.id} value={satellite.id}>
                          <Checkbox
                            checked={getValues('satellite_ids').includes(
                              satellite.id
                            )}
                          />
                          <ListItemText primary={satellite.name} />
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item>
            <Controller
              name='coord_system'
              control={control}
              render={({ field: { onChange } }) => (
                <FormControl fullWidth>
                  <InputLabel id='coord-system-select-label'>
                    Coordinate System
                  </InputLabel>
                  <Select
                    labelId='coord-system-select-label'
                    label='Coordinate System'
                    id='coord_system'
                    name='coord_system'
                    defaultValue='0'
                    onChange={(e) => onChange(e.target.value)}
                    value={'0'}
                  >
                    <MenuItem value={'0'}>Lat/Long</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item>
            <Controller
              name='coord_1'
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextField
                  id='coord_1'
                  name='coord_1'
                  label='Latitude'
                  type='number'
                  fullWidth
                  onChange={(e) => onChange(e.target.value)}
                  value={value}
                />
              )}
            />
          </Grid>
          <Grid item>
            <Controller
              name='coord_2'
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextField
                  id='coord_2'
                  name='coord_2'
                  label='Longitude'
                  type='number'
                  fullWidth
                  onChange={(e) => onChange(e.target.value)}
                  value={value}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color='secondary' variant='contained'>
          Cancel
        </Button>
        <Button
          color='primary'
          onClick={handleSubmit(onSubmit)}
          variant='contained'
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DishesDialog;
